import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import CancelButton from "../../components/cancelButton/CancelButton";
import { createParticipantListLink, listParticipantListLinks, revokeParticipantListLink } from "./participantListApi";

function participantListLinksQueryKey(orderId: string) {
  return ["orders", orderId, "participant-lists", "links"];
}

function participantListShareUrl(orderId: string, token: string): string {
  const encoded = encodeURIComponent(token);
  return `${window.location.origin}/public/orders/${orderId}/participant-lists?token=${encoded}`;
}

export interface ParticipantListLinksModalProps {
  readonly orderId: string;
  readonly open: boolean;
  readonly onClose: () => void;
}

const ParticipantListLinksModal = ({ orderId, open, onClose }: ParticipantListLinksModalProps) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [newlyCreatedUrl, setNewlyCreatedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setNewlyCreatedUrl(null);
    }
  }, [open]);

  const { data: links = [], isLoading } = useQuery({
    queryKey: participantListLinksQueryKey(orderId),
    queryFn: ({ signal }) => listParticipantListLinks(orderId, signal),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: () => createParticipantListLink(orderId),
    onSuccess: async (created) => {
      const url = participantListShareUrl(orderId, created.token);
      setNewlyCreatedUrl(url);
      await queryClient.invalidateQueries({ queryKey: participantListLinksQueryKey(orderId) });
      enqueueSnackbar(
        "Link został utworzony. Skopiuj go teraz — po zamknięciu okna pełny adres nie będzie już dostępny.",
        { variant: "success" },
      );
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się utworzyć linku. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (linkId: string) => revokeParticipantListLink(orderId, linkId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: participantListLinksQueryKey(orderId) });
      enqueueSnackbar("Link został cofnięty", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się cofnąć linku. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const handleCopyNewLink = async () => {
    if (!newlyCreatedUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(newlyCreatedUrl);
      enqueueSnackbar("Skopiowano do schowka", { variant: "success" });
    } catch {
      enqueueSnackbar("Nie udało się skopiować do schowka", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Udostępnianie listy uczestników</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{
            marginTop: 1,
          }}
        >
          {newlyCreatedUrl ? (
            <Stack spacing={1}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Nowy link (zapisz lub skopiuj teraz):
              </Typography>
              <TextField
                value={newlyCreatedUrl}
                fullWidth
                multiline
                minRows={2}
                slotProps={{ input: { readOnly: true } }}
              />
              <Button variant="outlined" onClick={() => void handleCopyNewLink()}>
                Kopiuj do schowka
              </Button>
            </Stack>
          ) : null}
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
            }}
          >
            Pełny adres linku widać tylko tu, zaraz po utworzeniu. Po zamknięciu okna możesz cofnąć aktywne linki w
            tabeli — bez ponownego wyświetlenia adresu.
          </Typography>
          {isLoading ? (
            <Typography variant="body2">Ładowanie linków…</Typography>
          ) : links.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Brak utworzonych linków.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Utworzono</TableCell>
                  <TableCell>Przez</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {links.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{new Date(row.createdAt).toLocaleString("pl-PL")}</TableCell>
                    <TableCell>{row.createdByEmail}</TableCell>
                    <TableCell>
                      {row.revokedAt ? `Cofnięty ${new Date(row.revokedAt).toLocaleString("pl-PL")}` : "Aktywny"}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => revokeMutation.mutate(row.id)}
                        disabled={!!row.revokedAt || revokeMutation.isPending}
                      >
                        Cofnij
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={() => onClose()}>Zamknij</CancelButton>
        <Button
          variant="contained"
          color="primary"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending}
        >
          Utwórz nowy link
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParticipantListLinksModal;
